/**
 * @file 主题组合式逻辑
 * @description 负责主题初始化、系统主题监听以及组件内主题切换入口。
 */
import {
  themeStore,
  type ResolvedTheme,
  type ThemeMode
} from '@/stores/modules/theme'
import { storeToRefs } from 'pinia'
import { watch, type Ref } from 'vue'

/** 主题组合式方法返回值 */
interface ThemeControls {
  /** 用户选择的主题模式引用 */
  mode: Ref<ThemeMode>
  /** 当前实际生效的主题引用 */
  resolvedTheme: Ref<ResolvedTheme>
  /** 当前是否为暗色主题引用 */
  isDark: Ref<boolean>
  /** 设置主题模式 */
  setMode: (nextMode: ThemeMode) => void
  /** 按 light / dark / system 顺序循环切换主题 */
  cycleMode: () => void
}

/** 系统暗色偏好媒体查询 */
const darkMediaQuery =
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null

/** 是否已注册系统偏好监听 */
let systemListenerRegistered = false

/**
 * 将解析后的主题应用到 document.documentElement
 * @param theme 实际主题
 */
export function applyThemeToDom(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') {
    return
  }
  document.documentElement.classList.toggle('dark', theme === 'dark')
  document.documentElement.style.colorScheme = theme
}

/**
 * 挂载前同步初始化主题，避免 FOUC
 */
export function initThemeBeforeMount(): void {
  const store = themeStore()
  applyThemeToDom(store.resolvedTheme)
}

/**
 * 注册系统偏好变化监听（mode 为 system 时生效）
 */
export function watchSystemPreference(): void {
  if (systemListenerRegistered || !darkMediaQuery) {
    return
  }
  systemListenerRegistered = true

  darkMediaQuery.addEventListener('change', () => {
    const store = themeStore()
    if (store.mode === 'system') {
      applyThemeToDom(store.resolvedTheme)
    }
  })
}

/**
 * 主题切换 composable
 * @returns 主题状态引用和主题切换方法
 */
export function useTheme(): ThemeControls {
  const store = themeStore()
  const { mode, resolvedTheme, isDark } = storeToRefs(store)

  watch(
    resolvedTheme,
    (theme) => {
      applyThemeToDom(theme)
    },
    { immediate: true }
  )

  /**
   * 设置主题模式
   * @param nextMode 目标模式
   */
  const setMode = (nextMode: ThemeMode): void => {
    store.setMode(nextMode)
    applyThemeToDom(store.resolvedTheme)
  }

  /** 循环切换 light / dark / system */
  const cycleMode = (): void => {
    store.cycleMode()
    applyThemeToDom(store.resolvedTheme)
  }

  return {
    mode,
    resolvedTheme,
    isDark,
    setMode,
    cycleMode
  }
}
